import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { agentPermissionHooks, agentsHooks, creationProductManager, iamAccountHooks, subscriptionHook } from "@/manager";
import {
  CollapseIcon,
  ExpandIcon,
} from "@/components/ui/icon";
import { SettingsMenu } from "@/components/settings-menu";
import { CreationSkeleton } from "@/components/common/creation-skeleton";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { AgentLogoIcon } from "@/components/agent/assets";
import { IconButton } from "../common/icon-button";
import { AssetsSidebar } from "../assets/assets-sidebar";
import { AgentSidebar } from "../agent/agent-sidebar";
import { Divider } from "../common/divider";
import { Tooltip } from "../ui/modal";
import { AgentCreditsIcon } from "../agent-creation/assets/agent-credits-icon";
import { Image } from "../common/image";
import { TruncatedText } from "../common/truncated-text";
import { CreditsModal } from "../agent-creation/credits-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function Index() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useI18nNavigate();
  const [collapse, setCollapse] = useState(false);
  const [hoverEnableCollapse, setHoverEnableCollapse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openCreditsModal, setOpenCreditsModal] = useState(false);

  // 刷新用户权限信息
  agentPermissionHooks.useInitialPermissionDetail();

  // 用户信息
  const { account } = iamAccountHooks.useData();
  // 当前智能体信息（包含智能体下的用户信息）
  const { agent, loading: agentLoading } = agentsHooks.useCurrentAgent({ isFetchTemplate: false });
  // 当前解析出来的智能体信息
  const { resolvedAgent } = agentsHooks.useGetCurrentAgent();
  const { agents } = agentsHooks.useGetAgentList();

  // 获取积分
  const { agentCredits, fetchAgentCredits, loading: creditsLoading } = subscriptionHook.useAgentCredits();

  // 判断用户是否订阅
  const isSubscribed = useMemo((() => {
    return agents?.some(item => item?.id === resolvedAgent?.id)
  }), [agents, resolvedAgent])

  console.log('Agent info in index:', { agents, agent, resolvedAgent, isSubscribed })
  useEffect(() => {
    return creationProductManager.loading((e) => {
      setLoading(e.detail);
    });
  }, []);

  // 处理折叠二级侧边栏的逻辑
  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  return (
    <div className={cn("flex h-screen bg-background")}>
      <aside
        className={cn(
          "relative z-10 flex flex-col transition-all duration-300 ease-in-out",
          collapse ? "min-w-[60px] max-w-[60px]" : "min-w-[260px] max-w-[260px]", // 动态宽度
          "bg-white border-r border-sidebar-border",
          "h-full pb-2 px-3 overflow-hidden"
        )}
      >
        <div className={cn("flex items-center transition-all duration-300 z-10 h-[62px] flex-shrink-0", collapse ? "justify-center" : "justify-between")}>
          <div
            className={cn(
              "ml-2 transition-all duration-300 ease-in-out overflow-hidden",
              collapse ? "w-0 opacity-0 pointer-events-none ml-0" : "w-[7rem] opacity-100",
              !collapse && resolvedAgent && "w-auto"
            )}
          >
            {!resolvedAgent && <AgentLogoIcon className="w-[5.5rem]" />}
            {resolvedAgent && (
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Avatar className={"size-7"}>
                  {resolvedAgent.avatar ? (
                    <Image src={resolvedAgent.avatar} alt={resolvedAgent.name} className="size-[30px] rounded-full flex-shrink-0" />
                  ) : (
                    <AvatarFallback className="bg-primary text-white">
                      {resolvedAgent.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                {/* <Image src={resolvedAgent.avatar} className="size-[30px] rounded-full flex-shrink-0" /> */}
                <TruncatedText text={resolvedAgent.name} className="text-md font-medium text-[rgba(13,13,13,1)]" />
              </div>
            )}
          </div>
          <Tooltip
            side='right'
            align='start'
            offset={20}
            trigger={
              <IconButton
                className={cn(
                  "size-9 !rounded-[6px] transition-all  hover:bg-[rgba(16,24,40,0.05)]",
                  collapse ? "cursor-e-resize" : "cursor-w-resize"
                )}
                icon={
                  collapse ? (
                    <CollapseIcon className="w-5 h-5 text-[rgba(13,13,13,1)]" />
                  ) : (
                    <ExpandIcon className="w-5 h-5 text-[rgba(13,13,13,1)]" />
                  )
                }
                onClick={handleCollapse}
              />
            }
            content={
              collapse && (
                <div className="relative max-w-[384px] w-max bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]">
                  <span className="block max-w-[384px] text-[rgba(255,255,255,1)] text-sm font-medium break-all">
                    {t("index.expand")}
                  </span>
                </div>
              )
            }
          />
        </div>
        <div className={cn(
          "w-full flex flex-col overflow-y-auto scrollbar-hide gap-2 z-10 pb-5",
          // collapse && "cursor-w-resize"
        )}>
          {/* 智能体侧边栏 */}
          <AgentSidebar
            collapsed={collapse}
          />
          {collapse && <Divider className="my-3" />}
          {/* 资产侧边栏 */}
          <AssetsSidebar
            collapsed={collapse}
          />
        </div>
        {/* 底部按钮 */}
        <div className="mt-auto">
          {!collapse && (
            <div className="mt-auto h-12 w-full mb-5 border rounded-[12px] flex items-center pl-2 gap-2 cursor-pointer active:scale-95 transition-all select-none" onClick={() => setOpenCreditsModal(true)}>
              <AgentCreditsIcon />
              <span>{agentCredits ?? '-'}</span>
            </div>
          )}
          <div className={cn(
            "flex items-center gap-[10px] border-t border-sidebar-border pt-[14px] px-2 pb-1.5 transition-all z-10",
            collapse && "items-center px-0"
          )}>
            <SettingsMenu agent={agent} isSubscribed={isSubscribed} />
            {!collapse && (
              <span className="text-sm font-medium text-[rgba(13,13,13,1)] whitespace-nowrap truncate">
                {isSubscribed ? (agent?.subName || account?.email) : account?.email}
              </span>
            )}
          </div>
        </div>
        {/* 可点击热区（注意：要给其他可点击元素增加层级 z-10） */}
        {collapse && (
          <div
            className={cn(
              "absolute top-0 right-0 h-full w-full",
              "cursor-e-resize",
            )}
            onClick={handleCollapse}
            onMouseEnter={() => setHoverEnableCollapse(true)}
            onMouseLeave={() => setHoverEnableCollapse(false)}
          />
        )}
        {openCreditsModal && (
          <CreditsModal
            open={openCreditsModal}
            onOpenChange={setOpenCreditsModal}
            credits={agentCredits}
          />
        )}
      </aside>
      <article className={cn("flex flex-1 w-full h-screen overflow-hidden")}>
        {loading ? <CreationSkeleton /> : <Outlet context={{ collapse }} />}
      </article>
    </div>
  );
}
